<template>
    <div class="fixed top-0 left-0 right-0 w-full bg-[var(--color-8)] dark:bg-transparent dark:bg-[linear-gradient(287.24deg,#1F0432_2.25%,#1D0231_93.17%)] z-50 border-b border-[#8a36c2]"
        :class="appendClass" @mouseover="handleMouseOver" @mouseout="handleMouseOut">
        <div class="max-w-screen-xl mx-auto px-4">
            <div class="flex items-center justify-between py-2">
                <div class="flex items-center space-x-3">
                    <div class="flex items-center justify-center gap-2">
                        <span class="text-sm font-semibold">Official Partner of </span>
                        <WrSVGIcon name="WrRight" class="size-3 fill-current" />
                    </div>

                    <div class="flex items-center gap-4">
                        <!-- Desktop -->
                        <template v-for="(icon, index) in techIcons" :key="index">
                            <NuxtLink v-if="icon?.alt == 'vaah<strong>cms</strong>'"
                                :to="'https://vaah.dev/cms?utm_source=webreinvent.com&utm_medium=top-menu&utm_campaign=flash'"
                                class="flex items-center justify-center gap-1 cursor-pointer hover:text-[var(--primary)] max-lg:hidden"
                                :target="'_blank'" :rel="'noopener noreferrer'">
                                <NuxtImg :src="$colorMode.preference == 'dark' ? icon.darkIcon : icon.src
                                    " :alt="icon?.alt" class="max-w-6" loading="eager" height="22" />

                                <p class="text-sm" v-html="icon?.alt"></p>
                            </NuxtLink>
                            <div v-else="icon?.alt !== 'vaah<strong>cms</strong>'"
                                class="flex items-center justify-center gap-1 cursor-pointer hover:text-[var(--primary)] max-lg:hidden"
                                @click="handleOpenModal(icon?.alt)">
                                <NuxtImg :src="$colorMode.preference == 'dark' ? icon.darkIcon : icon.src
                                    " :alt="icon?.alt" class="max-w-6" loading="eager" height="22" />

                                <p class="text-sm" v-html="icon?.alt"></p>
                            </div>
                        </template>

                        <!-- Mobile -->
                        <div class="w-20 h-5 relative lg:hidden">
                            <transition mode="out-in"
                                enter-active-class="absolute transition-opacity duration-[1000ms] ease-in-out delay-[100ms]"
                                enter-from-class="opacity-0" enter-to-class="opacity-100"
                                leave-active-class="absolute transition-opacity duration-[500ms] ease-in-out"
                                leave-from-class="opacity-100" leave-to-class="opacity-0">
                                <div :key="techIcons[currentIndex].alt"
                                    class="partner-link flex items-center justify-center gap-1 cursor-pointer hover:text-[var(--primary)] absolute left-0 top-0 w-full h-full"
                                    @click="handleOpenModal(techIcons[currentIndex].alt)">
                                    <NuxtImg :src="$colorMode.preference == 'dark'
                                            ? techIcons[currentIndex].darkIcon
                                            : techIcons[currentIndex].src
                                        " :alt="techIcons[currentIndex].alt" class="max-w-6" loading="eager" height="22" />
                                    <p class="text-sm" v-html="techIcons[currentIndex].alt"></p>
                                </div>
                            </transition>
                        </div>
                    </div>
                </div>

                <div class="flex items-center justify-center space-x-2 max-md:hidden">
                    <div v-if="isLocating" class="flex items-center space-x-2">
                        <div class="h-5 w-8 bg-gray-300 dark:bg-gray-700 rounded-full animate-pulse"></div>
                        <div class="h-4 w-48 bg-gray-300 dark:bg-gray-700 rounded animate-pulse"></div>
                        <div class="size-3 bg-gray-300 dark:bg-gray-700 rounded animate-pulse"></div>
                    </div>
                    <a v-else target="_blank" rel="noopener noreferrer" :href="isIndia
                            ? 'https://webreinvent.ohoapps.com/hrm/v4#/jobs'
                            : '/services/maintenance-services'
                        " class="font-semibold flex items-center space-x-2 text-[var(--foreground)] hover:text-[var(--primary)] tracking-[0.5px]">
                        <span
                            class="bg-[#bf1839] text-white text-[11px] font-medium px-2 py-1 rounded-full tracking-[0.5px]">
                            NEW
                        </span>
                        <span class="text-sm">
                            {{
                                isIndia
                                    ? "we're hiring!"
                                    : "Get maintenance and support services starting from $49"
                            }}
                        </span>
                        <WrSVGIcon name="WrRight" class="size-3 fill-current" />
                    </a>
                </div>
            </div>
        </div>
    </div>
    <WrOfficialPartnerModal class="w-full" :is-open="isVisible" @close="isVisible = false" />
</template>

<script setup>
import { ref, onMounted, onBeforeUnmount } from "vue";
import { defineAsyncComponent } from "vue";

defineProps({
  appendClass: {
    type: String,
    default: "",
  },
});

const WrOfficialPartnerModal = defineAsyncComponent(
    () => import("../molecules/WrOfficialPartnerModal.vue"),
);

const isIndia = ref(false);
const isLocating = ref(true);
let partnerRotationInterval = null;

const detectLocation = () => {
    fetch("https://ipwho.is/")
        .then((res) => res.json())
        .then((data) => {
            isIndia.value = data?.country === "India";
            isLocating.value = false;
        })
        .catch(() => {
            try {
                const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
                if (tz === "Asia/Calcutta" || tz === "Asia/Kolkata") {
                    isIndia.value = true;
                }
            } catch (e) {
                console.error("Timezone detection failed:", e);
            }
            isLocating.value = false;
        });
};

onMounted(() => {
    if (process.client) {
        detectLocation();

        partnerRotationInterval = setInterval(() => {
            currentIndex.value = (currentIndex.value + 1) % techIcons.length;
        }, 2000);
    }
});

onBeforeUnmount(() => {
    if (partnerRotationInterval) {
        clearInterval(partnerRotationInterval);
        partnerRotationInterval = null;
    }
    document.body.style.overflow = "";
});

const techIcons = [
    {
        src: "/images/icons/icon-laravel-dark.svg",
        alt: "Laravel",
        darkIcon: "/images/icons/icon-laravel-dark.svg",
    },
    {
        src: "/images/icons/nuxt.webp",
        alt: "Nuxt",
        darkIcon: "/images/icons/nuxt.webp",
    },
    {
        src: "/images/icons/flutter.webp",
        alt: "Flutter",
        darkIcon: "/images/icons/flutter.webp",
    },
    {
        src: "/images/icons/Vue.webp",
        alt: "Vue",
        darkIcon: "/images/icons/Vue.webp",
    },
    {
        src: "/images/vaahCmsDarkIcon.svg",
        alt: "vaah<strong>cms</strong>",
        darkIcon: "/images/vaahCmsDarkIcon.svg",
    },
];

const currentIndex = ref(0);
const isVisible = ref(false);

const handleMouseOver = (event) => {
    document.body.style.overflow = "hidden";
};

const handleMouseOut = (event) => {
    const header = event.currentTarget;
    if (!header.contains(event.relatedTarget)) {
        document.body.style.overflow = "";
    }
};

const handleOpenModal = (linkName) => {
    if (linkName === "vaah<strong>cms</strong>") {
        window.open(
            "https://vaah.dev/cms?utm_source=webreinvent.com&utm_medium=top-menu&utm_campaign=flash",
            "_blank",
        );
    } else {
        isVisible.value = true;
    }
};
</script>

<style scoped>
@keyframes shimmer {
    0% {
        opacity: 0.6;
    }

    50% {
        opacity: 1;
    }

    100% {
        opacity: 0.6;
    }
}

.animate-pulse {
    animation: shimmer 1.5s ease-in-out infinite;
}
</style>
